import {useEffect, useState} from "react";
import {isMobile} from "react-device-detect";
import {AppBar, Box, Stack, Typography} from "@mui/material";
import clsx from "clsx";
import {useSelector, useDispatch} from "store";
import {set} from "store/reducers/modal";
import {signout} from "store/reducers/auth";
import {getJackpot} from "api/jackpot";
import {logout} from "api/user";
import {JackpotDetailType} from "types";
import SettingDialogView from "sections/setting";
import Iconify from "components/iconify";

import HeaderBackground from "assets/images/header.png";

import SettingIcon from "assets/images/icons/setting.png";
import UserIcon from "assets/images/icons/user.png";
import LogoutIcon from "assets/images/icons/logout.png";

type JackpotKeys = "mega" | "major" | "minor" | "mini";

function Header() {
    const dispatch = useDispatch();
    const {isLoggedIn, user} = useSelector((state) => state.auth);

    const [settingModalStatus, setSettingModalStatus] = useState(false);
    const [isOpenItem, setIsOpenItem] = useState(false);

    const [jackpot, setJackpot] = useState<Record<JackpotKeys, number>>({
        mega: 0,
        major: 0,
        minor: 0,
        mini: 0,
    });

    const openAuthModal = (value: string) => {
        dispatch(set(value));
    };

    const openSettingModal = () => {
        setSettingModalStatus(true);
    };

    const getJackpotData = async () => {
        const response = await getJackpot();
        if (response && response?.jackpots) {
            const transformedObject = response?.jackpots.reduce(
                (acc: Record<string, number>, jackpot: JackpotDetailType) => {
                    const key = jackpot.name.toLowerCase();
                    acc[key] = jackpot.dropAmount ?? jackpot.startAmount;
                    return acc;
                },
                {} as Record<string, number>
            );

            setJackpot(transformedObject);
        }
    };

    useEffect(() => {
        if (isLoggedIn) getJackpotData();
    }, []);

    const handleUserLogout = async () => {
        setIsOpenItem(!isOpenItem);

        await logout();

        localStorage.clear();
        dispatch(signout());
    };

    const handleClickSetting = () => {
        setIsOpenItem(!isOpenItem);
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    backgroundImage: `url(${HeaderBackground})`,
                    backgroundSize: "120% 100%",
                    boxShadow: "none",
                    height: {xs: "30px", md: "40px"},
                }}
            >
                <Stack direction="row" justifyContent="space-around" alignItems="center" height={1}>
                    <Stack direction="row" gap={{xs: 1, md: 2}}>
                        <Box sx={{fontSize: {xs: 15, md: 20}, fontWeight: 500, display: "flex", alignItems: "center"}}>
                            MINI JACKPOT
                        </Box>
                        <Box sx={{color: "yellow", fontSize: {xs: 15, md: 20}, fontWeight: 900}}>
                            {jackpot.mini || 0}
                        </Box>
                    </Stack>
                    <Stack direction="row" gap={{xs: 1, md: 2}}>
                        <Box sx={{fontSize: {xs: 15, md: 20}, fontWeight: 500, display: "flex", alignItems: "center"}}>
                            MEGA JACKPOT
                        </Box>
                        <Box sx={{color: "yellow", fontSize: {xs: 15, md: 20}, fontWeight: 900}}>
                            {jackpot.mega || 0}
                        </Box>
                    </Stack>
                    <Stack direction="row" gap={{xs: 1, md: 2}}>
                        <Box sx={{fontSize: {xs: 15, md: 20}, fontWeight: 500, display: "flex", alignItems: "center"}}>
                            MAJOR JACKPOT
                        </Box>
                        <Box sx={{color: "yellow", fontSize: {xs: 15, md: 20}, fontWeight: 900}}>
                            {jackpot.major || 0}
                        </Box>
                    </Stack>
                </Stack>
            </AppBar>

            {isLoggedIn && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={isMobile ? 0.5 : 1}
                        sx={{position: "absolute", bottom: "4%", left: "3%", zIndex: 200}}
                    >
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                width: {xs: "40px", md: "100px"},
                                height: {xs: "40px", md: "100px"},
                                borderRadius: "50px",
                                backgroundImage:
                                    "radial-gradient(circle at center, #191e2b 0%, #033458 47%, #191e2b 99%, #191e2b 100%)",
                                border: "3px solid #373e5c",
                                boxSizing: "border-box",
                                cursor: "pointer",
                            }}
                        >
                            <Iconify icon="fa:user" width="75%" height="75%" color="#fffc00" />
                        </Stack>

                        <Typography variant={isMobile ? "body1" : "h4"} color="#fffc00">
                            {user.name.replace(/\s/g, "")}
                        </Typography>
                    </Stack>

                    <Stack
                        gap={isOpenItem ? {xs: 1, md: 3} : 0}
                        sx={{
                            position: "absolute",
                            width: {xs: "54px", md: "116px"},
                            borderRadius: {xs: "32px", md: "55px"},
                            backgroundColor: "#021621",
                            border: `${isMobile ? 2 : 3}px solid #373e5c`,
                            boxSizing: "border-box",
                            bottom: "4%",
                            right: "3%",
                            cursor: "pointer",
                            zIndex: 200,
                            padding: "5px",
                        }}
                    >
                        <Box className={clsx("menu-wrapper", {open: isOpenItem})}>
                            <Stack gap={{xs: 1, md: 3}}>
                                <Stack
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{
                                        width: {xs: "40px", md: "100px"},
                                        height: {xs: "40px", md: "100px"},
                                        borderRadius: "50px",
                                        backgroundImage:
                                            "radial-gradient(circle at center, #191e2b 0%, #033458 47%, #191e2b 99%, #191e2b 100%)",
                                        border: "3px solid #373e5c",
                                        boxSizing: "border-box",
                                    }}
                                    onClick={handleUserLogout}
                                >
                                    <Box component="img" src={LogoutIcon} width="75%" height="75%" />
                                </Stack>
                                <Stack
                                    justifyContent="center"
                                    alignItems="center"
                                    sx={{
                                        width: {xs: "40px", md: "100px"},
                                        height: {xs: "40px", md: "100px"},
                                        borderRadius: "50px",
                                        backgroundImage:
                                            "radial-gradient(circle at center, #191e2b 0%, #033458 47%, #191e2b 99%, #191e2b 100%)",
                                        border: "3px solid #373e5c",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Box component="img" src={UserIcon} height="75%" />
                                </Stack>
                            </Stack>
                        </Box>

                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                width: {xs: "40px", md: "100px"},
                                height: {xs: "40px", md: "100px"},
                                borderRadius: "50px",
                                backgroundImage:
                                    "radial-gradient(circle at center, #191e2b 0%, #033458 47%, #191e2b 99%, #191e2b 100%)",
                                border: "3px solid #373e5c",
                                boxSizing: "border-box",
                            }}
                            onClick={handleClickSetting}
                        >
                            <Box component="img" src={SettingIcon} width="75%" height="75%" />
                        </Stack>
                    </Stack>
                </>
            )}

            <SettingDialogView status={settingModalStatus} onClose={() => setSettingModalStatus(false)} />

            {/* <BackgroundAudio />
            <ButtonClickAudio ref={buttonClickAudioRef} /> */}
        </>
    );
}

export default Header;
